import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, TrendingUp, ChevronRight } from "lucide-react";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";

const bandColor = {
  "Bank Ready": "bg-emerald-500",
  "Conditionally Ready": "bg-amber-500",
  "Preparation Required": "bg-orange-500",
  "Insufficient Information": "bg-slate-500",
};

export default function Readiness() {
  const [biz, setBiz] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, tv } = useLang();

  useEffect(() => {
    (async () => {
      const r = await api.get("/businesses");
      const b = r.data[0]; setBiz(b);
      if (b) {
        try {
          const { data } = await api.post(`/businesses/${b.id}/assess-readiness`);
          setResult(data);
        } catch (e) { toast.error("Assessment failed"); }
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="text-slate-500">{t("r_assessing")}</div>;
  if (!biz) return <div className="text-slate-600">Please create a business profile first. <Link className="text-orange-600 font-semibold" to="/borrower/passport">Go to Passport</Link></div>;
  if (!result) return <div className="text-slate-600">Assessment unavailable.</div>;

  const score = result.score, band = result.band;
  const bandBar = bandColor[band] || "bg-slate-500";

  return (
    <div className="space-y-6" data-testid="readiness-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 brand-heading">{t("r_title")}</h1>
        <p className="text-slate-600 mt-1 text-sm">{t("r_sub")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 p-8 rounded-lg border border-slate-200 bg-white">
          <div className="text-xs uppercase tracking-widest text-slate-500">{t("r_band")}</div>
          <div className="mt-1 text-2xl font-bold text-slate-900 brand-heading" data-testid="readiness-band-label">{tv(band)}</div>
          <div className="mt-6 relative">
            <div className="text-5xl font-bold text-slate-900 brand-heading" data-testid="readiness-score">{score}<span className="text-lg font-normal text-slate-500">/100</span></div>
            <div className="mt-4 h-3 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full ${bandBar} transition-all`} style={{ width: `${score}%` }} />
            </div>
          </div>
          <div className="mt-6 space-y-2">
            {Object.entries(result.scores || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-xs">
                <div className="text-slate-600 capitalize">{tv(k) === k ? k.replace(/_/g, " ") : tv(k)}</div>
                <div className="text-slate-900 font-semibold">{v}/{result.weights[k]}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="p-6 rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /><h2 className="font-semibold text-slate-900 brand-heading">{t("r_strengths")}</h2></div>
            <ul className="mt-3 space-y-1 text-sm text-slate-700">
              {(result.strengths || []).map((s) => <li key={s}>· {tv(s)}</li>)}
              {result.strengths?.length === 0 && <li className="text-slate-500">{t("r_no_strengths")}</li>}
            </ul>
          </div>
          <div className="p-6 rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /><h2 className="font-semibold text-slate-900 brand-heading">{t("r_warnings")}</h2></div>
            <ul className="mt-3 space-y-1 text-sm text-slate-700">
              {(result.warnings || []).map((s) => <li key={s}>· {tv(s)}</li>)}
              {(result.missing || []).map((s) => <li key={s} className="text-slate-500">· {tv(s)} ({t("r_missing")})</li>)}
            </ul>
          </div>
          <div className="p-6 rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-orange-500" /><h2 className="font-semibold text-slate-900 brand-heading">{t("r_actions")}</h2></div>
            <ul className="mt-3 space-y-1 text-sm text-slate-700">
              {(result.recommended_actions || []).map((s) => <li key={s}>· {tv(s)}</li>)}
              {result.recommended_actions?.length === 0 && <li className="text-slate-500">{t("r_all_ok")}</li>}
            </ul>
          </div>
          <div className="p-6 rounded-lg border border-slate-200 bg-white">
            <h2 className="font-semibold text-slate-900 brand-heading">{t("r_routes")}</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {(result.suitable_routes || []).map((r) => (
                <span key={r} className="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-xs" data-testid={`route-${r}`}>{tv(r)}</span>
              ))}
            </div>
            <Link to="/borrower/routes" className="mt-4 inline-flex items-center gap-1 text-sm text-slate-900 font-semibold hover:text-orange-600">{t("r_explore")} <ChevronRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900 leading-relaxed">
        <strong>{t("r_disc_label")}</strong> {t("r_disc")}
      </div>
      <DisclaimerBanner compact />
    </div>
  );
}
